import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import plusIcon from "../../../assets/img/icons/plus_white.svg";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import {
  ButtonIcon,
  ColoredButtonWithIcon,
} from "../../../styledComponents/buttons";
import {
  ColoredText,
  CommonFlex,
  CommonGrid,
  CommonText,
  FormAlert,
  InputLabel,
  InputWithLabelSection,
  TableText,
} from "../../../styledComponents/common";
import { TableItem } from "../../../styledComponents/invoice";
import { currencySymbolMap } from "../../../utils/currencies";
import LineItemModal from "../../JobComponents/LineItemModal";
import LineItemsTable from "../../JobComponents/LineItemsTable";
import { Label } from "../../UserProfile/profileStyles";
import { CommonCardContainer } from "components/DashboardCards/cardStyles";
import {
  CalculationContainer,
  GrandTotalAmount,
  GrandTotalAmountSection,
  GrandTotalContainer,
  GrandTotalLabel,
  GrandTotalLabelSection,
  LineItemContainer,
  TaxAmountLabel,
  TaxTypeButton,
} from "../invoiceStyles";
import TaxAmountSection from "./TaxAmountSection";
import { InputField } from "../../../styledComponents/common";

const InvoiceLineItemSection = props => {
  const {
    setLineItems,
    pageType,
    lineItems,
    currency,
    selectedVisitJobs,
    selectedJob,
    createInvoiceItem,
    updateInvoiceItem,
    deleteInvoiceItem,
    updateInvoiceTax,
    updateInvoiceDiscount,
    invoiceDetails,
    selectedOrganization,
    isEditMode,
  } = props;

  const { t } = useTranslation();
  const [action, setAction] = useState("add");
  const [popupOpen, setPopupOpen] = useState(false);
  const [dataForEdit, setDataForEdit] = useState(null);

  // const [currency, setCurrency] = useState({
  //   label: 'USD ($) - United States Dollar',
  //   value: 'USD',
  // });

  const [itemArray, setItemArray] = useState([]);
  const [totalCalc, setTotalCalc] = useState(0);
  const [tax1, setTax1] = useState({
    name: "",
    value: "",
    percent: true,
    isChanged: false,
  });
  const [tax2, setTax2] = useState({
    name: "",
    value: "",
    percent: true,
    isChanged: false,
  });
  const [discount, setDiscount] = useState({
    value: "",
    percent: true,
    isChanged: false,
  });
  const [grandTotalValue, setGrandTotalValue] = useState(0);
  const [saveErrors, setSaveErrors] = useState(null);

  // Load tax and discount info when in edit mode
  useEffect(() => {
    if (isEditMode && invoiceDetails) {
      if (invoiceDetails.tax1) {
        setTax1({
          name: invoiceDetails.tax1.name || "",
          value:
            invoiceDetails.tax1.type === "percent"
              ? invoiceDetails.tax1.percent_amount
              : invoiceDetails.tax1.fixed_amount,
          percent: invoiceDetails.tax1.type === "percent",
          id: invoiceDetails.tax1.id,
        });
      }
      if (invoiceDetails.tax2) {
        setTax2({
          name: invoiceDetails.tax2.name || "",
          value:
            invoiceDetails.tax2.type === "percent"
              ? invoiceDetails.tax2.percent_amount
              : invoiceDetails.tax2.fixed_amount,
          percent: invoiceDetails.tax2.type === "percent",
          id: invoiceDetails.tax2.id,
        });
      }
      if (invoiceDetails.discount) {
        setDiscount({
          value:
            invoiceDetails.discount.type === "percent"
              ? invoiceDetails.discount.percent_amount
              : invoiceDetails.discount.fixed_amount,
          percent: invoiceDetails.discount.type === "percent",
          id: invoiceDetails.discount.id,
        });
      }
    }
  }, [isEditMode, invoiceDetails]);

  // Load items from invoiceDetails when in edit mode
  useEffect(() => {
    if (
      isEditMode &&
      invoiceDetails?.items &&
      invoiceDetails.items.length > 0
    ) {
      const loadedItems = invoiceDetails.items.map(item => ({
        id: item.id,
        itemType: item.type === "service" ? "Service" : "Product",
        name: item.description || "", // Using description as name since API doesn't provide name
        description: item.description || "",
        quantity: item.quantity,
        unitPrice: item.unit_price,
        amount: item.quantity * item.unit_price,
        tax1Toggle: item.tax1,
        tax2Toggle: item.tax2,
        evidenceToggle: false,
      }));
      setItemArray(loadedItems);
    }
  }, [isEditMode, invoiceDetails?.items]);

  // useEffect(() => {

  // }, [orgCurrency])

  useEffect(() => {
    totalCalculation();

    // Send item Array to Parent component
    if (itemArray?.length > 0) {
      const payload = itemArray.map((item, index) => ({
        item_type: item.itemType?.toLowerCase(),
        description: item.description,
        serial_number: index + 1,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        tax1: item.tax1Toggle,
        tax2: item.tax2Toggle,
        name: item.name,
      }));
      setLineItems(payload);
    }
  }, [itemArray]);

  useEffect(() => {
    grandTotalCalculation();
  }, [itemArray, tax1, tax2, discount, totalCalc]);

  // Populate line items from selected visit jobs' subtasks
  useEffect(() => {
    // Skip this logic in edit mode - items are already loaded from invoiceDetails
    if (isEditMode) {
      return;
    }

    // Keep manually added items (items without subtaskId)
    const manualItems = itemArray.filter(item => !item.subtaskId);

    const subtaskItems = [];

    // Process selectedJob (single job)
    if (selectedJob && selectedJob.value) {
      // Try to find the full job data (with subtasks) from the parent component
      // The selectedJob might only have basic info, but we can still check for subtasks
      if (selectedJob.subtasks && selectedJob.subtasks.length > 0) {
        selectedJob.subtasks.forEach(subtask => {
          subtaskItems.push({
            itemType: "Service",
            name: subtask.name,
            description: subtask.description || "",
            quantity: subtask.quantity,
            unitPrice: subtask.unit_price,
            amount: subtask.quantity * subtask.unit_price,
            evidenceToggle: false,
            tax1Toggle: true,
            tax2Toggle: true,
            subtaskId: subtask.id,
            jobId: selectedJob.value,
          });
        });
      }
    }

    // Process selectedVisitJobs (recurring jobs with multiple dates)
    if (selectedVisitJobs && selectedVisitJobs.length > 0) {
      selectedVisitJobs.forEach(job => {
        if (job.subtasks && job.subtasks.length > 0) {
          job.subtasks.forEach(subtask => {
            subtaskItems.push({
              itemType: "Service",
              name: subtask.name,
              description: subtask.description || "",
              quantity: subtask.quantity,
              unitPrice: subtask.unit_price,
              amount: subtask.quantity * subtask.unit_price,
              evidenceToggle: false,
              tax1Toggle: true,
              tax2Toggle: true,
              subtaskId: subtask.id,
              jobId: job.id,
            });
          });
        }
      });
    }

    // Combine manual items with subtask items
    if (subtaskItems.length > 0) {
      setItemArray([...manualItems, ...subtaskItems]);
    } else {
      // When no jobs are selected, keep only manual items
      setItemArray(manualItems);
    }
  }, [selectedJob, selectedVisitJobs, isEditMode]);

  const handleDeleteItem = index => {
    const item = itemArray[index];

    // For EditInvoice: Call API to delete if item has an ID
    if (isEditMode && item?.id && deleteInvoiceItem) {
      const payload = {
        organization_id: selectedOrganization?.id,
        invoice_id: invoiceDetails?.id,
        item_id: item.id,
      };
      deleteInvoiceItem(payload);
    }

    // Always remove from local state
    let newArray = [...itemArray];
    newArray.splice(index, 1);
    setItemArray(newArray);
  };

  const totalCalculation = () => {
    if (itemArray && itemArray.length) {
      let total = 0;
      itemArray.map(item => {
        total += item.quantity * item.unitPrice;
      });
      setTotalCalc(total.toFixed(2));
    }
  };

  const handleEditItem = index => {
    let item = itemArray[index];
    setDataForEdit({ ...item, index });
    setAction("edit");
    setPopupOpen(true);
  };

  const handleAddItems = items => {
    // Handle Edit Case
    if (items.action == "edit") {
      // For EditInvoice: Call API to update item
      if (isEditMode && items.id && updateInvoiceItem) {
        const payload = {
          organization_id: selectedOrganization?.id,
          invoice_id: invoiceDetails?.id,
          item_id: items.id,
          type: items.itemType?.toLowerCase(),
          description: items.description,
          quantity: items.quantity,
          unit_price: items.unitPrice,
          allow_evidences: items.evidenceToggle,
          name: items.name,
        };
        updateInvoiceItem(payload);
      }

      let newArray = [...itemArray];
      newArray[items.index] = {
        itemType: items.itemType,
        name: items.name,
        description: items.description,
        quantity: items.quantity,
        unitPrice: items.unitPrice,
        amount: items.amount,
        evidenceToggle: items.evidenceToggle,
        tax1Toggle: items.tax1Toggle,
        tax2Toggle: items.tax2Toggle,
        id: items.id,
      };
      setItemArray(newArray);
      return;
    }

    // Handle Add Case
    // For EditInvoice: Call API to create item
    if (isEditMode && createInvoiceItem) {
      const payload = {
        invoice_id: invoiceDetails?.id,
        type: items.itemType?.toLowerCase(),
        description: items.description,
        quantity: items.quantity,
        unit_price: items.unitPrice,
        allow_evidences: items.evidenceToggle,
        name: items.name,
        organization_id: selectedOrganization?.id,
      };
      createInvoiceItem(payload);
    }

    // For CreateInvoice: Just add to local state
    let newArray = [...itemArray, items];
    setItemArray(newArray);
  };

  const handleTax1ToggleClick = index => {
    let newArr = [...itemArray];
    newArr[index].tax1Toggle = !newArr[index].tax1Toggle;

    // Call API to update item in edit mode
    if (isEditMode && newArr[index]?.id && updateInvoiceItem) {
      const item = newArr[index];
      const payload = {
        organization_id: selectedOrganization?.id,
        invoice_id: invoiceDetails?.id,
        item_id: item.id,
        type: item.itemType?.toLowerCase(),
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        allow_evidences: item.evidenceToggle,
        tax1: item.tax1Toggle,
        tax2: item.tax2Toggle,
        name: item.name,
      };
      updateInvoiceItem(payload);
    }

    setItemArray(newArr);
  };

  const handleTax2ToggleClick = index => {
    let newArr = [...itemArray];
    newArr[index].tax2Toggle = !newArr[index].tax2Toggle;

    // Call API to update item in edit mode
    if (isEditMode && newArr[index]?.id && updateInvoiceItem) {
      const item = newArr[index];
      const payload = {
        organization_id: selectedOrganization?.id,
        invoice_id: invoiceDetails?.id,
        item_id: item.id,
        type: item.itemType?.toLowerCase(),
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        allow_evidences: item.evidenceToggle,
        tax1: item.tax1Toggle,
        tax2: item.tax2Toggle,
        name: item.name,
      };
      updateInvoiceItem(payload);
    }

    setItemArray(newArr);
  };

  const onTax1NameChange = e => {
    setTax1({ ...tax1, name: e.target.value, isChanged: true });
  };

  const onTax1ValueChange = e => {
    let errors = { ...saveErrors };
    delete errors[`tax1Value`];
    setSaveErrors(errors);

    setTax1({ ...tax1, value: e.target.value, isChanged: true });
  };

  const toggleTax1CalcType = value => {
    setTax1({
      ...tax1,
      percent: value,
      isChanged: true,
    });
  };

  const onTax2NameChange = e => {
    setTax2({ ...tax2, name: e.target.value, isChanged: true });
  };

  const onTax2ValueChange = e => {
    let errors = { ...saveErrors };
    delete errors[`tax2Value`];
    setSaveErrors(errors);

    setTax2({ ...tax2, value: e.target.value, isChanged: true });
  };

  const toggleTax2CalcType = value => {
    setTax2({
      ...tax2,
      percent: value,
      isChanged: true,
    });
  };

  const onDiscountValueChange = e => {
    let errors = { ...saveErrors };
    delete errors[`discountValue`];
    delete errors[`discountFixed`];
    setSaveErrors(errors);

    setDiscount({ ...discount, value: e.target.value, isChanged: true });
  };

  const toggleDiscountCalcType = value => {
    setDiscount({
      ...discount,
      percent: value,
      isChanged: true,
    });
  };

  const grandTotalCalculation = () => {
    const calcaulateTaxDiscount = (object, cost) => {
      if (object.percent) {
        return (object.value * cost) / 100;
      } else if (object.value) {
        return object.value;
      } else {
        return 0;
      }
    };

    if (itemArray && itemArray.length) {
      let totalTax = 0;
      itemArray.map((item, index) => {
        let itemCost = item.quantity * item.unitPrice;
        if (item.tax1Toggle) {
          totalTax += parseFloat(calcaulateTaxDiscount(tax1, itemCost));
        }
        if (item.tax2Toggle) {
          totalTax += parseFloat(calcaulateTaxDiscount(tax2, itemCost));
        }
      });

      let grandTotal =
        parseFloat(totalCalc) +
        totalTax -
        parseFloat(calcaulateTaxDiscount(discount, totalCalc));
      setGrandTotalValue(grandTotal.toFixed(2));
    }
  };

  const handleAddItemClick = () => {
    setAction("add");
    setDataForEdit(null);
    setPopupOpen(true);
  };

  const handleSaveTaxDiscount = () => {
    // Save tax1 if changed
    if (tax1.isChanged && isEditMode && updateInvoiceTax) {
      const payload = {
        tax_item_id: invoiceDetails?.tax1?.id,
        invoice_id: invoiceDetails?.id,
        tax_type: "tax1",
        name: tax1.name,
        type: tax1.percent ? "percent" : "fixed",
        ...(tax1.percent
          ? { percent_amount: tax1.value }
          : { fixed_amount: tax1.value }),
        organization_id: selectedOrganization?.id,
      };
      updateInvoiceTax(payload);
      setTax1({ ...tax1, isChanged: false });
    }

    // Save tax2 if changed
    if (tax2.isChanged && isEditMode && updateInvoiceTax) {
      const payload = {
        tax_item_id: invoiceDetails?.tax2?.id,
        invoice_id: invoiceDetails?.id,
        tax_type: "tax2",
        name: tax2.name,
        type: tax2.percent ? "percent" : "fixed",
        ...(tax2.percent
          ? { percent_amount: tax2.value }
          : { fixed_amount: tax2.value }),
        organization_id: selectedOrganization?.id,
      };
      updateInvoiceTax(payload);
      setTax2({ ...tax2, isChanged: false });
    }

    // Save discount if changed
    if (discount.isChanged && isEditMode && updateInvoiceDiscount) {
      const payload = {
        discount_id: invoiceDetails?.discount?.id,
        invoice_id: invoiceDetails?.id,
        type: discount.percent ? "percent" : "fixed",
        ...(discount.percent
          ? { percent_amount: discount.value }
          : { fixed_amount: discount.value }),
        organization_id: selectedOrganization?.id,
      };
      updateInvoiceDiscount(payload);
      setDiscount({ ...discount, isChanged: false });
    }
  };

  return (
     <CommonCardContainer style={{ minWidth: '1200px', marginTop: "6px"}} bgColor="#EDF8FE">
      <LineItemModal
        isOpen={popupOpen}
        toggle={() => setPopupOpen(!popupOpen)}
        addItems={handleAddItems}
        action={action}
        dataForEdit={dataForEdit}
        currency={currency}
        pageType="invoice"
      />
      <CommonFlex disableResponsive margin="0 0 16px 0" justifyContent="space-between" alignItems="center">
        <CommonText name title fontSize="16px">
          Line Items
        </CommonText>
        <ColoredButtonWithIcon
          type="orange"
          onClick={() => handleAddItemClick()}
        >
          <ButtonIcon src={plusIcon} alt="plus" />
          Add Line Items
        </ColoredButtonWithIcon>
      </CommonFlex>

      <div>
        {itemArray && itemArray.length > 0 ? (
          <LineItemsTable
            itemArray={itemArray}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            onTax1ToggleClick={handleTax1ToggleClick}
            onTax2ToggleClick={handleTax2ToggleClick}
            totalCalc={totalCalc}
            currency={currency}
            pageType="invoice"
          />
        ) : (
          <CommonText>No line items added yet</CommonText>)}
        </div>
        {/* calculation section */}
        

        <CommonFlex disableResponsive  justifyContent="flex-end" style={{ marginTop: "20px" }} alignItems="center">
          <CommonFlex disableResponsive width="418px" direction="column" alignItems="flex-end" gap="16px">
            <CommonGrid>
<InputWithLabelSection>
            <TaxAmountLabel>Tax 1 Name</TaxAmountLabel>
            <InputField
              type="text"
              placeholder="Enter Tax 1 Name"
              value={tax1.name}
              onChange={e => onTax1NameChange(e)}
            />
          </InputWithLabelSection>
          <TaxAmountSection
            value={tax1.value}
            onChange={e => onTax1ValueChange(e)}
            isPercentage={tax1.percent}
            onTypeChange={isPercent => toggleTax1CalcType(isPercent)}
            currency={currency}
          />
          </CommonGrid>
          <CommonGrid disableResponsive>
          <InputWithLabelSection>
            <TaxAmountLabel>Tax 2 Name</TaxAmountLabel>
            <InputField
              type="text"
              placeholder="Enter Tax 2 Name"
              value={tax2.name}
              onChange={e => onTax2NameChange(e)}
            />
          </InputWithLabelSection>
          <TaxAmountSection
            value={tax2.value}
            onChange={e => onTax2ValueChange(e)}
            isPercentage={tax2.percent}
            onTypeChange={isPercent => toggleTax2CalcType(isPercent)}
            currency={currency}
          />
          </CommonGrid>
          <TaxAmountSection
            label="Discount"
            value={discount.value}
            onChange={e => onDiscountValueChange(e)}
            isPercentage={discount.percent}
            onTypeChange={isPercent => toggleDiscountCalcType(isPercent)}
            currency={currency}
          />
          <GrandTotalContainer>
            <GrandTotalLabelSection>
              <GrandTotalLabel>Grand Total</GrandTotalLabel>
            </GrandTotalLabelSection>
            <GrandTotalAmountSection>
              <GrandTotalAmount>
                {currency && currencySymbolMap[currency] ? currencySymbolMap[currency] : '$'}
                {grandTotalValue || "0.00"}
              </GrandTotalAmount>
            </GrandTotalAmountSection>
          </GrandTotalContainer>

          {isEditMode &&
            (tax1.isChanged || tax2.isChanged || discount.isChanged) && (
              <div style={{ marginRight: `auto`, marginTop: "10px" }}>
                <ColoredButtonWithIcon
                  type="orange"
                  onClick={handleSaveTaxDiscount}
                >
                  Save
                </ColoredButtonWithIcon>
              </div>
            )}
          </CommonFlex>
        </CommonFlex>
      
    </CommonCardContainer>
  );
};

export default InvoiceLineItemSection;
